import RichTextEditor, { BaseKit } from "reactjs-tiptap-editor";
import { Node, mergeAttributes } from "@tiptap/core";
import { Image } from "reactjs-tiptap-editor/image";
import { Video } from "reactjs-tiptap-editor/video";
import { Blockquote } from "reactjs-tiptap-editor/blockquote";
import { Bold } from "reactjs-tiptap-editor/bold";
import { Italic } from "reactjs-tiptap-editor/italic";
import { Strike } from "reactjs-tiptap-editor/strike";
import { Heading } from "reactjs-tiptap-editor/heading";
import { Link } from "reactjs-tiptap-editor/link";
import { TextAlign } from "reactjs-tiptap-editor/textalign";
import { TextUnderline } from "reactjs-tiptap-editor/textunderline";
import { TrailingNode } from "reactjs-tiptap-editor/trailingnode";
import { TextBubble } from "reactjs-tiptap-editor/textbubble";
import { OrderedList } from "reactjs-tiptap-editor/orderedlist";
import { History } from "reactjs-tiptap-editor/history";
import { Highlight } from "reactjs-tiptap-editor/highlight";
import { Clear } from "reactjs-tiptap-editor/clear";
import { Color } from "reactjs-tiptap-editor/color";
import { FontFamily } from "reactjs-tiptap-editor/fontfamily";
import { FontSize } from "reactjs-tiptap-editor/fontsize";
import { BulletList } from "reactjs-tiptap-editor/bulletlist";
import { useEffect, useRef } from "react";
import { Plugin, PluginKey } from "prosemirror-state";

//  ONE stable key (outside component)
const openInNewTabKey = new PluginKey("open-in-new-tab");

const CustomLink = Link.extend({
  //  THIS is what actually removes target="_blank"
  renderHTML({ HTMLAttributes }) {
    const { href, target, rel, ...rest } = HTMLAttributes;

    return [
      "a",
      {
        ...rest,
        "data-href": href, // store URL
        role: "link",
        tabindex: "0", // focusable
        contenteditable: "false",
        draggable: "false",
      },
      0,
    ];
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      contenteditable: { default: "false" },
      draggable: { default: false },
      tabindex: { default: "0" },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: openInNewTabKey,
        props: {
          handleClick(view, pos, event) {
            const el = event.target;
            if (!(el instanceof HTMLAnchorElement)) return false;

            const href = el.getAttribute("href");
            if (!href) return false;

            event.preventDefault();
            window.open(href, "_blank", "noopener,noreferrer");
            return true;
          },

          handleKeyDown(view, event) {
            if (event.key !== "Enter") return false;

            const { state } = view;
            const linkMark = state.selection.$from
              .marks()
              .find((m) => m.type.name === "link");

            if (!linkMark) return false;

            event.preventDefault();
            window.open(linkMark.attrs.href, "_blank", "noopener,noreferrer");
            return true;
          },
        },
      }),
    ];
  },
});

// Fully inert paragraph
const CustomParagraph = Node.create({
  name: "paragraph",
  group: "block",
  content: "inline*",
  parseHTML() {
    return [{ tag: "p" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "p",
      mergeAttributes(HTMLAttributes, { contenteditable: "false" }),
      0,
    ];
  },
});

const RenderMessage = ({ content }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    console.log("Setting up inert editor link handlers", container);
    if (!containerRef.current) return;

    const openLink = (el) => {
      const url = el.getAttribute("data-href");
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    };

    const handler = (e) => {
      const link = e.target.closest("a[data-href]");
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();
      openLink(link);
    };

    const keyHandler = (e) => {
      if (e.key === "Enter" || e.key === " ") handler(e);
    };

    container.addEventListener("click", handler, true);
    container.addEventListener("keydown", keyHandler, true);

    return () => {
      container.removeEventListener("click", handler, true);
      container.removeEventListener("keydown", keyHandler, true);
    };
  }, []); // only run once

  useEffect(() => {
    const interval = setInterval(() => {
      const editors = document.querySelectorAll(
        ".richtext-relative .tiptap.ProseMirror"
      );
      if (editors.length) {
        editors.forEach((editorDiv) => {
          editorDiv.setAttribute("contenteditable", "false");
          editorDiv.setAttribute("tabindex", "-1");
        });
        clearInterval(interval);
      }
    }, 50);
  }, []);

  const extensions = [
    BaseKit.configure({
      characterCount: false,
      actions: false,
      placeholder: false,
    }),
    History,
    Clear,
    FontFamily,
    Heading,
    FontSize,
    Bold,
    Italic,
    Strike,
    TextUnderline,
    Color,
    Highlight,
    BulletList,
    OrderedList,
    TextAlign,
    CustomLink.configure({
      openOnClick: false,
    }),
    Blockquote,
    TrailingNode,
    TextBubble,
    Image,
    Video.configure({ width: "600" }),
    CustomParagraph,
  ];

  return (
    <div
      className="richtext-inert"
      role="region"
      aria-label="Message content"
      ref={containerRef}
    >
      <RichTextEditor
        content={content} // controlled content
        extensions={extensions}
        onChange={() => {}}
        toolbar={false} // hide toolbar
        editable={false} // disables typing
        output="html"
        dark={false}
      />
      {
        <style jsx>{`
          /* Make entire editor fully inert */
          .richtext-py-2 {
            padding: 0px !important;
            display: none !important;
          }
          .richtext-relative {
            pointer-events: none !important; /* disable all clicks */
            /* hide browser spellcheck underlines (squiggles) */
            -webkit-text-decoration-color: transparent !important;
            text-decoration-color: transparent !important;
          }
          /* Allow clickable elements inside */
          .richtext-relative a {
            cursor: pointer !important;
            pointer-events: auto !important; /* enable clicks only here */
          }
          .richtext-relative iframe,
          .richtext-relative video,
          .richtext-relative img[role="media"] {
            pointer-events: auto !important; /* enable clicks only here */
          }
          .tippy-content {
            display: none !important;
          }

          /* editor inert */
          .richtext-inert .ProseMirror {
            pointer-events: none;
          }

          /* re-enable links */
          .richtext-inert .ProseMirror a {
            pointer-events: auto;
            cursor: pointer;
          }
        `}</style>
      }
    </div>
  );
};

export default RenderMessage;
